/**
 * Writing a module (#190): the pages a trainee will work through, and the
 * check at the end.
 *
 * One list of pages with a kind each, rather than a form per kind, because
 * a module is read in order and written in order — the thing a manager is
 * doing is arranging it. A page of drinks names them off the book rather
 * than copying them in, so the page says what to teach and the book says
 * what it is.
 */

import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_PASS_MARK,
  MAX_CHOICES,
  recipeName,
  type Recipe,
  type TrainingModule,
  type TrainingPage,
  type TrainingPageKind,
  type TrainingQuestion,
  type TrainingStatus,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { trainingApi } from "./api.js";
import "./training.css";

const message = (err: unknown): string =>
  err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not save";

const STATUS_LABEL: Readonly<Record<TrainingStatus, string>> = {
  draft: "Draft — only you can see it",
  published: "Published — everyone works through it",
  retired: "Retired — off the course",
};

let seq = 0;
const freshId = (prefix: string) => `${prefix}${Date.now().toString(36)}${(seq++).toString(36)}`;

function blankPage(kind: TrainingPageKind): TrainingPage {
  const pageId = freshId("p");
  if (kind === "image") return { kind, pageId, heading: "", caption: "", imageKey: "" };
  if (kind === "drinks") return { kind, pageId, heading: "", body: "", recipeIds: [], tag: null };
  return { kind, pageId, heading: "", body: "" };
}

interface Draft {
  readonly moduleId: string | null;
  readonly title: string;
  readonly summary: string;
  readonly status: TrainingStatus;
  readonly position: number;
  readonly passMark: number;
  readonly version: number;
  readonly pages: readonly TrainingPage[];
  readonly questions: readonly TrainingQuestion[];
}

const blank: Draft = {
  moduleId: null,
  title: "",
  summary: "",
  status: "draft",
  position: 0,
  passMark: DEFAULT_PASS_MARK,
  version: 0,
  pages: [],
  questions: [],
};

export function ModuleEditor(p: {
  /** Null to write a new one. */
  readonly moduleId: string | null;
  readonly onDone: (moduleId: string) => void;
  readonly onCancel: () => void;
}) {
  const app = useApp();
  const { token } = app;
  const [draft, setDraft] = useState<Draft>(blank);
  const [loading, setLoading] = useState(p.moduleId !== null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recipes = app.library.recipes ?? [];
  const items = useMemo(() => new Map((app.library.items ?? []).map((i) => [i.itemId, i])), [app.library.items]);
  const nameOf = (r: Recipe) => {
    const item = items.get(r.itemId);
    return item ? recipeName(item, r) : r.label;
  };
  const byName = useMemo(
    () => [...recipes].sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    [recipes, items],
  );
  const tags = useMemo(() => {
    const all = new Set<string>();
    for (const r of recipes) for (const t of r.tags ?? []) all.add(t);
    for (const i of app.library.items ?? []) for (const t of i.tags ?? []) all.add(t);
    return [...all].sort();
  }, [recipes, app.library.items]);

  useEffect(() => {
    if (!p.moduleId) return;
    let live = true;
    trainingApi
      .module(token, p.moduleId)
      .then((res) => {
        if (!live) return;
        const m: TrainingModule = res.module;
        setDraft({
          moduleId: m.moduleId,
          title: m.title,
          summary: m.summary,
          status: m.status,
          position: m.position,
          passMark: m.passMark,
          version: m.version,
          pages: m.pages ?? [],
          questions: m.questions ?? [],
        });
        setLoading(false);
      })
      .catch((err) => {
        if (!live) return;
        setError(message(err));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token, p.moduleId]);

  const patch = (over: Partial<Draft>) => setDraft((d) => ({ ...d, ...over }));
  const setPage = (at: number, over: Partial<TrainingPage>) =>
    setDraft((d) => ({
      ...d,
      pages: d.pages.map((pg, i) => (i === at ? ({ ...pg, ...over } as TrainingPage) : pg)),
    }));
  const movePage = (at: number, by: number) =>
    setDraft((d) => {
      const to = at + by;
      if (to < 0 || to >= d.pages.length) return d;
      const next = [...d.pages];
      const held = next[at]!;
      next[at] = next[to]!;
      next[to] = held;
      return { ...d, pages: next };
    });
  const setQuestion = (at: number, over: Partial<TrainingQuestion>) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, i) => (i === at ? ({ ...q, ...over } as TrainingQuestion) : q)),
    }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const { module } = await trainingApi.save(token, {
        ...(draft.moduleId ? { moduleId: draft.moduleId } : {}),
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        status: draft.status,
        position: draft.position,
        passMark: draft.passMark,
        version: draft.version,
        pages: draft.pages,
        questions: draft.questions,
      });
      p.onDone(module.moduleId);
    } catch (err) {
      setError(message(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="pad label">Opening…</div>;

  return (
    <div className="screen tr-edit">
      <div className="row screen-head tr-head">
        <div style={{ minWidth: 0 }}>
          <button className="label tr-back" onClick={p.onCancel}>← Back</button>
          <h2 className="tr-title">{draft.moduleId ? "Edit module" : "New module"}</h2>
        </div>
        <span className="spacer" />
        <button className="btn" onClick={p.onCancel} disabled={saving}>Cancel</button>
        <button className="btn primary" onClick={() => void save()} disabled={saving || !draft.title.trim()}>
          {saving ? "Saving…" : draft.moduleId ? "Save" : "Create"}
        </button>
      </div>

      <div className="scroll tr-edit-body">
        {error && <div className="msg err">{error}</div>}

        <div className="tr-fields">
          <label className="field">
            <span>Title</span>
            <input type="text" value={draft.title} onChange={(e) => patch({ title: e.target.value })} placeholder="Opening the bar" />
          </label>
          <label className="field">
            <span>A line about it</span>
            <input type="text" value={draft.summary} onChange={(e) => patch({ summary: e.target.value })} placeholder="What happens before the doors." />
          </label>
          <label className="field">
            <span>Status</span>
            <select value={draft.status} onChange={(e) => patch({ status: e.target.value as TrainingStatus })}>
              {(["draft", "published", "retired"] as const).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Where it sits in the course</span>
            <input className="num-input" type="number" min={0} max={9999} value={draft.position} onChange={(e) => patch({ position: Number(e.target.value) || 0 })} />
          </label>
          <label className="field">
            <span>Pass the check at (%)</span>
            <input className="num-input" type="number" min={0} max={100} step={5} value={draft.passMark} onChange={(e) => patch({ passMark: Number(e.target.value) || 0 })} />
          </label>
        </div>

        {/* ------------------------------------------------------- pages */}
        <div className="label tr-section">Pages · {draft.pages.length}</div>
        {draft.pages.map((page, at) => (
          <div className="tr-edit-card" key={page.pageId}>
            <div className="tr-edit-top">
              <span className="label">
                {at + 1} · {page.kind === "drinks" ? "Drinks" : page.kind === "image" ? "Picture" : "Writing"}
              </span>
              <span className="spacer" />
              <button className="btn sm" disabled={at === 0} onClick={() => movePage(at, -1)} aria-label="Move up">↑</button>
              <button className="btn sm" disabled={at === draft.pages.length - 1} onClick={() => movePage(at, 1)} aria-label="Move down">↓</button>
              <button className="btn sm danger" onClick={() => patch({ pages: draft.pages.filter((_, i) => i !== at) })} aria-label="Remove the page">×</button>
            </div>

            <label className="field">
              <span>Heading</span>
              <input type="text" value={page.heading} onChange={(e) => setPage(at, { heading: e.target.value })} />
            </label>

            {page.kind === "prose" && (
              <label className="field">
                <span>What it says</span>
                <textarea rows={6} value={page.body} onChange={(e) => setPage(at, { body: e.target.value })} />
              </label>
            )}

            {page.kind === "image" && <ImagePage page={page} onChange={(over) => setPage(at, over)} />}

            {page.kind === "drinks" && (
              <>
                <label className="field">
                  <span>What it says (optional)</span>
                  <textarea rows={3} value={page.body} onChange={(e) => setPage(at, { body: e.target.value })} />
                </label>
                <label className="field">
                  <span>Everything tagged</span>
                  <select value={page.tag ?? ""} onChange={(e) => setPage(at, { tag: e.target.value || null })}>
                    <option value="">No tag — just the drinks below</option>
                    {tags.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <div className="label">
                  Named drinks · {page.recipeIds.length}. These are read off the book, so a recipe that changes
                  changes the training.
                </div>
                <div className="tr-picks">
                  {page.recipeIds.map((recipeId) => {
                    const recipe = recipes.find((r) => r.recipeId === recipeId);
                    return (
                      <button
                        key={recipeId}
                        className="btn sm"
                        onClick={() => setPage(at, { recipeIds: page.recipeIds.filter((r) => r !== recipeId) })}
                      >
                        {recipe ? nameOf(recipe) : `${recipeId} (not in the book)`} ×
                      </button>
                    );
                  })}
                </div>
                <select
                  value=""
                  aria-label="Add a drink to this page"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setPage(at, { recipeIds: [...new Set([...page.recipeIds, e.target.value])] });
                  }}
                >
                  <option value="">+ Add a drink…</option>
                  {byName.map((r) => <option key={r.recipeId} value={r.recipeId}>{nameOf(r)}</option>)}
                </select>
              </>
            )}
          </div>
        ))}

        <div className="tr-adds">
          {(["prose", "image", "drinks"] as const).map((kind) => (
            <button
              key={kind}
              className="btn dashed"
              onClick={() => patch({ pages: [...draft.pages, blankPage(kind)] })}
            >
              + {kind === "prose" ? "Writing" : kind === "image" ? "Picture" : "Drinks"}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------- the check */}
        <div className="label tr-section">The check · {draft.questions.length}</div>
        {draft.questions.map((question, at) => (
          <div className="tr-edit-card" key={question.questionId}>
            <div className="tr-edit-top">
              <span className="label">{at + 1} · {question.kind === "build" ? "What goes in it" : "Multiple choice"}</span>
              <span className="spacer" />
              <button className="btn sm danger" onClick={() => patch({ questions: draft.questions.filter((_, i) => i !== at) })} aria-label="Remove the question">×</button>
            </div>

            {question.kind === "build" ? (
              <label className="field">
                <span>The drink</span>
                <select value={question.recipeId} onChange={(e) => setQuestion(at, { recipeId: e.target.value })}>
                  <option value="">Pick a drink…</option>
                  {byName.map((r) => <option key={r.recipeId} value={r.recipeId}>{nameOf(r)}</option>)}
                </select>
              </label>
            ) : (
              <>
                <label className="field">
                  <span>The question</span>
                  <input type="text" value={question.prompt} onChange={(e) => setQuestion(at, { prompt: e.target.value })} />
                </label>
                <div className="label">The answers. Tick the right one.</div>
                {question.choices.map((choice, c) => (
                  <div className="tr-choice-edit" key={c}>
                    <input
                      type="radio"
                      name={`answer-${question.questionId}`}
                      checked={question.answer === c}
                      aria-label={`Answer ${c + 1} is right`}
                      onChange={() => setQuestion(at, { answer: c })}
                    />
                    <input
                      type="text"
                      value={choice}
                      onChange={(e) =>
                        setQuestion(at, { choices: question.choices.map((x, i) => (i === c ? e.target.value : x)) })
                      }
                    />
                    <button
                      className="btn sm danger"
                      disabled={question.choices.length <= 2}
                      aria-label="Remove this answer"
                      onClick={() =>
                        setQuestion(at, {
                          choices: question.choices.filter((_, i) => i !== c),
                          answer: question.answer > c ? question.answer - 1 : Math.min(question.answer, question.choices.length - 2),
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  className="btn sm dashed"
                  disabled={question.choices.length >= MAX_CHOICES}
                  onClick={() => setQuestion(at, { choices: [...question.choices, ""] })}
                >
                  + Another answer
                </button>
              </>
            )}

            <label className="field">
              <span>Why (shown after it is marked)</span>
              <input type="text" value={question.why} onChange={(e) => setQuestion(at, { why: e.target.value })} />
            </label>
          </div>
        ))}

        <div className="tr-adds">
          <button
            className="btn dashed"
            onClick={() =>
              patch({
                questions: [
                  ...draft.questions,
                  { kind: "choice", questionId: freshId("q"), prompt: "", choices: ["", ""], answer: 0, why: "" },
                ],
              })
            }
          >
            + Multiple choice
          </button>
          <button
            className="btn dashed"
            onClick={() =>
              patch({
                questions: [
                  ...draft.questions,
                  { kind: "build", questionId: freshId("q"), recipeId: byName[0]?.recipeId ?? "", why: "" },
                ],
              })
            }
          >
            + What goes in a drink
          </button>
        </div>

        {draft.moduleId && (
          <div className="tr-danger">
            <button
              className="btn danger"
              disabled={saving}
              onClick={async () => {
                if (!window.confirm(`Delete “${draft.title}”? What people have already done stays on their record.`)) return;
                setSaving(true);
                try {
                  await trainingApi.remove(token, draft.moduleId!);
                  p.onDone("");
                } catch (err) {
                  setError(message(err));
                } finally {
                  setSaving(false);
                }
              }}
            >
              Delete this module
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** A picture page: upload straight to the bucket, keep the key. */
function ImagePage(p: {
  readonly page: Extract<TrainingPage, { kind: "image" }>;
  readonly onChange: (over: Partial<Extract<TrainingPage, { kind: "image" }>>) => void;
}) {
  const { token } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!p.page.imageKey) return setPreview(null);
    let live = true;
    trainingApi
      .imageUrl(token, p.page.imageKey)
      .then((res) => live && setPreview(res.url))
      .catch(() => live && setPreview(null));
    return () => {
      live = false;
    };
  }, [token, p.page.imageKey]);

  return (
    <>
      <label className="field">
        <span>What the picture shows</span>
        <input
          type="text"
          value={p.page.caption}
          placeholder="The garnish, cut and sitting on the rim"
          onChange={(e) => p.onChange({ caption: e.target.value })}
        />
      </label>
      <div className="label">Said out loud to anyone who cannot see it, so it is not optional.</div>

      {preview && <img className="tr-preview" src={preview} alt={p.page.caption} />}
      {error && <div className="msg err">{error}</div>}

      <label className="btn dashed tr-upload">
        {busy ? "Uploading…" : p.page.imageKey ? "Replace the picture" : "Choose a picture"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setBusy(true);
            setError(null);
            try {
              const { key, url } = await trainingApi.presign(token, file.type, file.size);
              await trainingApi.upload(url, file);
              p.onChange({ imageKey: key });
            } catch (err) {
              setError(message(err));
            } finally {
              setBusy(false);
            }
          }}
        />
      </label>
    </>
  );
}
